'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { installHistoryTraversalTracking } from '@/core/utils/historyTraversal';

/**
 * One global top progress bar for every client-side navigation, instead of
 * the previous page sitting frozen (no loading.tsx on most routes) until the
 * next one hard-swaps in all at once.
 *
 * The App Router has no public "navigation started" event — history.pushState
 * only fires once the new route's RSC payload has already arrived, which is
 * too late to show a "loading" state for. So navigation START is detected by
 * intercepting clicks on internal <a> links (before the router has a chance
 * to fetch anything), and navigation END by watching pathname/searchParams
 * actually change.
 */
export function GlobalRouteProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const tickRef = useRef<number | null>(null);
  const hideRef = useRef<number | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    installHistoryTraversalTracking();
  }, []);

  const clearTimers = () => {
    if (tickRef.current !== null) { window.clearInterval(tickRef.current); tickRef.current = null; }
    if (hideRef.current !== null) { window.clearTimeout(hideRef.current); hideRef.current = null; }
  };

  const start = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    clearTimers();
    setVisible(true);
    setProgress(12);
    tickRef.current = window.setInterval(() => {
      setProgress((p) => (p >= 90 ? p : p + (90 - p) * 0.15));
    }, 180);
  };

  const finish = () => {
    if (!startedRef.current) return;
    startedRef.current = false;
    clearTimers();
    setProgress(100);
    hideRef.current = window.setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 200);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement)?.closest?.('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      try {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) return;
        if (url.pathname === window.location.pathname && url.search === window.location.search) return;
        start();
      } catch {
        // relative/invalid href — ignore
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    finish();
    // Safety net: a click can "start" progress without the URL ever actually
    // changing (blocked navigation, confirm dialog, same-route action) — don't
    // leave the bar stuck forever in that case.
    const safety = window.setTimeout(() => finish(), 6000);
    return () => window.clearTimeout(safety);
  }, [pathname, searchParams]);

  useEffect(() => () => clearTimers(), []);

  if (!visible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] h-[3px] pointer-events-none overflow-hidden">
      <div
        className="h-full w-full bg-primary origin-left"
        style={{
          transform: `scaleX(${progress / 100})`,
          opacity: progress >= 100 ? 0 : 1,
          transition: 'transform 0.3s ease-out, opacity 0.3s ease-in',
          willChange: 'transform, opacity',
        }}
      />
    </div>
  );
}
